import { useContext, useEffect } from "react";
import Router, { useRouter } from "next/router";
import { ApolloError } from "@apollo/client";
import { AuthContext, AuthStatus, SessionInfo } from "@/contexts/AuthContext";
import {
  useSignInWithConnectBoardMutation,
  useConnectToConnectBoardMutation,
  RegistrationTokenType,
} from "@/generated/graphql";
import type Rollbar from "rollbar";
import { useSnackBar } from "@moneyforward/mfx_biz_react_components";
import {
  ErrorCode,
  errorCodeIsIncluded,
  extractConnBoardRqeustErrorMessage,
  moveToConnBoardRequestErrorPage,
  moveToErrorPage,
} from "@/libs/errors";
import { getUrlWithQuery } from "@/libs/url";
import useFixedAuth from "@/libs/useFixedAuth";
import { setLoginSource, LoginSource } from "@/libs/loginSource";
import config from "@/libs/config";
import useRollbarUtil from "../hooks/useRollbarUtil";

export const ConnectBoardCallBackPage = () => {
  const { rollbar } = useRollbarUtil();
  // State and Variables
  const clientID = config.COGNITO_CLIENT_ID;
  const redirectURI = config.CONNECT_BOARD_REDIRECT_URI;
  const app = config.APP;
  const { setSignInUserSession, signOut } = useContext(AuthContext);
  const { authStatus } = useFixedAuth();
  const { notify } = useSnackBar();
  const router = useRouter();

  // GraphQL Hooks
  const [
    connectToConnectBoardMutation,
    { called: connectToConnectBoardCalled },
  ] = useConnectToConnectBoardMutation();

  const connectToConnectBoard = (authCode: string) => {
    connectToConnectBoardMutation({
      variables: {
        input: {
          client_id: clientID,
          code: authCode,
          redirect_uri: redirectURI,
        },
      },
      onCompleted: async (data) => {
        const token = data.connectToConnectBoard.token;
        const session: SessionInfo = {
          accessToken: token.access_token,
          idToken: token.id_token,
          refreshToken: token.refresh_token,
        };
        await setSignInUserSession(session);
        notify(`${config.CONNECT_BOARD_DISPLAY_NAME}との連携が完了しました`);
        setLoginSource(LoginSource.connectboard);
        router.push(getUrlWithQuery("/select-service", app));
      },
      onError: config.IS_DHBK
        ? onConnectToConnectBoardErrorDhbk(app, signOut, rollbar)
        : handleError(rollbar),
    });
  };

  const [
    signInWithConnectBoardMutation,
    { called: signInWithConnectBoardCalled },
  ] = useSignInWithConnectBoardMutation();

  const signInWithConnectBoard = (authCode: string) => {
    signInWithConnectBoardMutation({
      variables: {
        input: {
          client_id: clientID,
          code: authCode,
          redirect_uri: redirectURI,
        },
      },
      onCompleted: async (data) => {
        const authToken = data.signInWithConnectBoard.token;
        const registrationToken = data.signInWithConnectBoard.registrationToken;

        const queryParams = {};
        if (app) {
          queryParams["app"] = app;
        }

        // 認証トークンが得られた場合サインイン状態にする
        if (authToken) {
          const session: SessionInfo = {
            accessToken: authToken.access_token,
            idToken: authToken.id_token,
            refreshToken: authToken.refresh_token,
          };
          await setSignInUserSession(session);
          notify("ログインが完了しました");
          setLoginSource(LoginSource.connectboard);
          router.push(getUrlWithQuery("/select-service", app));
          return;
        }

        // 認証トークンが得られなかった場合、登録トークンを使い事業者またはユーザーを登録する
        if (!registrationToken) {
          // 想定外のケース
          moveToErrorPage(ErrorCode.CONNBOARD_INTERNAL_SERVER_ERROR);
          return;
        }
        queryParams["code"] = registrationToken.code;

        if (registrationToken.type == RegistrationTokenType.Company) {
          // 事業者登録フローへ進む
          router.push({
            pathname: "/connect-biz/provisional-registration",
            query: queryParams,
          });
          return;
        }
        if (registrationToken.type == RegistrationTokenType.User) {
          // ユーザー登録フローへ進む
          router.push({
            pathname: "/connect-biz/register-user",
            query: queryParams,
          });
          return;
        }

        // 想定外のケース
        moveToErrorPage(ErrorCode.CONNBOARD_INTERNAL_SERVER_ERROR);
      },
      // すでにログイン済みの場合は、onCompleted が呼ばれないので、ここで処理する
      onError: config.IS_DHBK
        ? onSignInWithConnectBoardErrorDhbk(rollbar)
        : handleError(rollbar),
    });
  };

  // Main Process
  useEffect(() => {
    if (!router.isReady) {
      return;
    }

    // == 仕様確認中↓↓ ==
    // code, state はconnectBoard のauth endpoint からのリダイレクト時に付与される。
    // iss はconnectBoard からの相互遷移機能によるリダイレクト時に付与される。
    // == 仕様確認中↑↑ ==
    const { code, state, iss } = router.query; // FIXME: use state
    const codeStr = Array.isArray(code) ? code[0] : code;

    // 相互遷移機能で遷移してきた場合、connectBoard にリダイレクトして認可コードを取得する。
    if (iss) {
      router.push("/connect-biz/redirect-login");
      return;
    }

    // connectBoard auth endpoint からのリダイレクトでない場合connectBoard ログイン画面に遷移。
    if (!codeStr) {
      router.push("/connect-biz/redirect-login");
      return;
    }

    if (authStatus == AuthStatus.Loading) {
      return;
    }

    if (connectToConnectBoardCalled || signInWithConnectBoardCalled) {
      // signInWithConnectBoard.onCompleted でサインイン状態になると
      // useEffect が実行されconnectToConnectBoard が呼ばれてしまうため、
      // どちらかが既に呼ばれた場合はreturn する。
      return;
    }

    // ログイン状態の場合、ConnectBoard 未連携と仮定して連携フローに進む。
    if (authStatus == AuthStatus.SignedIn) {
      connectToConnectBoard(codeStr);
      return;
    }

    // 未ログイン状態の場合、ConnectBoard 連携済みと仮定してログインフローに進む。
    signInWithConnectBoard(codeStr);
  }, [authStatus, router.isReady]);

  return <></>;
};

const onConnectToConnectBoardErrorDhbk =
  (app: string, signOut: () => void, rollbar: Rollbar) =>
  (err: ApolloError) => {
    // == エラーとみなす場合 ==
    // ログイン済みでsubが異なる場合サインアウトさせる
    if (
      errorCodeIsIncluded(
        err,
        ErrorCode.CONNBOARD_USER_IS_CONNECTED_TO_ANOTHER_SUBJECT
      )
    ) {
      signOut();
      handleError(rollbar)(err); // サインアウト後にエラーを表示する
    }

    // == エラーとみなさない場合 ==
    // [Note] Bizsolと違い、CONNBOARD_USER_IS_ALREADY_CONNECTED は返ってこず、Mutation.onCompleted で処理する
    // そのため、 CONNBOARD_USER_IS_ALREADY_CONNECTED の if ブロックは結合テスト時に成功することを確認した後削除予定
    // ログイン済み & 連携済み の場合はエラーにせず次に移動する
    if (
      errorCodeIsIncluded(err, ErrorCode.CONNBOARD_USER_IS_ALREADY_CONNECTED)
    ) {
      // ログイン元をCBに上書きする
      // -> このパスを通る時点で、 LastLoginSource は Mikatano になっているはず
      setLoginSource(LoginSource.connectboard);
      // 正常処理として次のページに遷移する
      const query = app ? { app: app } : {};
      Router.push({
        pathname: "/select-service",
        query: query,
      });
      return;
    }
    // ログイン済み & NOT 連携済み は起こり得ないケースなのでエラーにする
    handleError(rollbar)(err);
    return;
  };

const onSignInWithConnectBoardErrorDhbk =
  (rollbar: Rollbar) => (err: ApolloError) => {
    // [Note] Bizsol-kyoshin と異なり権限による登録不可といったエラーは発生しない
    handleError(rollbar)(err);
  };

// connectBoard 系のAPI に対する共通のエラー処理.
const handleError = (rollbar: Rollbar) => (err: ApolloError) => {
  const msg = extractConnBoardRqeustErrorMessage(err);
  if (msg) {
    // connectBoard へのAPI Request が失敗した場合、メッセージをそのまま表示する
    moveToConnBoardRequestErrorPage(msg);
    return;
  }

  const errorList: Array<ErrorCode> = [
    ErrorCode.CONNBOARD_COMPANY_ALREADY_REGISTERED,
    ErrorCode.CONNBOARD_USER_IS_NOT_ALLOWED_TO_REGISTER_COMPANY,
    ErrorCode.CONNBOARD_USER_IS_NOT_ALLOWED_TO_SIGN_IN,
    ErrorCode.CONNBOARD_USER_IS_NOT_ALLOWED_TO_CONNECT,
    ErrorCode.CONNBOARD_TOKEN_IS_EXPIRED,
    ErrorCode.CONNBOARD_INVALID_ID_TOKEN,
    ErrorCode.CONNBOARD_USER_IS_CONNECTED_TO_ANOTHER_SUBJECT,
  ];

  errorList.forEach((code) => {
    if (errorCodeIsIncluded(err, code)) {
      moveToErrorPage(code);
      return;
    }
  });

  rollbar.error("Failed to connect to connectBoard", err);
  moveToErrorPage(ErrorCode.CONNBOARD_INTERNAL_SERVER_ERROR); // 上記にないエラーでもconnectBoard ボタンのあるエラーページに遷移する
};
